/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Framework, frameworks, Group, Standard, standards } from '@g17eco/core';
import { Scope } from '../model/surveyData';
import { Tags, UniversalTrackerPlain } from '../types/universalTracker';

// No Questions Available for these codes
const excludedFrameworkCodes = ['cdsb-req11'];

export const getStandards = (surveyScope?: Scope) =>
  Object.values(standards).filter((s) => (s.hidden ? surveyScope?.standards?.includes(s.code) : true));
export const getFrameworks = (surveyScope?: Scope) =>
  Object.values(frameworks).filter((s) => (s.hidden ? surveyScope?.frameworks?.includes(s.code) : true));

export interface FrameworkOption {
  code: string;
  value: string;
  searchString: string;
  label: JSX.Element;
}

export interface PackOption extends Omit<FrameworkOption, 'code'> {
  scopeType: 'standards' | 'frameworks' | 'custom';
}

interface GetPackOptions {
  showShortName?: boolean
}

export const getFrameworksOptions = (surveyScope?: Scope) => {
  const getOption = (f: Group, parentGroup: Group) => {
    return (
      <span>
        <img src={f.src ?? parentGroup.src} alt={f.name} height='26px' className='mr-2' />
        <span>{f.name}</span>
      </span>
    );
  };
  const frameworksOptions: FrameworkOption[] = [];
  getFrameworks(surveyScope).forEach((framework: Framework) => {
    framework.subgroups?.forEach((f: Group) => {
      if (!excludedFrameworkCodes.includes(f.code)) {
        frameworksOptions.push({
          value: f.code,
          searchString: `${framework.code} ${f.name ?? ''}`,
          label: getOption(f, framework),
        } as FrameworkOption);
      }
    });
  });
  return frameworksOptions;
};

export const getRootFrameworksOptions = (options?: GetPackOptions): PackOption[] => {
  return [
    ...getFrameworks().map((framework: Framework) => ({
      value: framework.code,
      searchString: `${framework.code} ${framework.name}`,
      label: (
        <span>
          <img src={framework.src} alt={framework.name} height='26px' className='mr-2' />
          <span>{options?.showShortName ? framework.shortName : framework.name}</span>
        </span>
      ),
      scopeType: 'frameworks',
    }) as PackOption),
  ];
};

export const getStandardsOptions = (surveyScope?: Scope, options?: GetPackOptions): PackOption[] => {
  return [
    ...getStandards(surveyScope).map((standard: Standard) => ({
      value: standard.code,
      searchString: `${standard.code} ${standard.name ?? ''}`,
      label: (
        <span>
          <img src={standard.src} alt={standard.name} height='26px' className='mr-2' />
          <span>{options?.showShortName ? standard.shortName : standard.name}</span>
        </span>
      ),
      scopeType: 'standards',
    }) as PackOption),
  ];
};

export const getStandardsFrameworksValues = () =>
  [...getStandards(), ...getFrameworks()].map((item) => ({ label: item.name, value: item.code }));

export const SURVEY_PACKS = [...getStandardsOptions(), ...getRootFrameworksOptions()];
export const SURVEY_PACKS_SHORT_NAME = [...getStandardsOptions(undefined, { showShortName: true }), ...getRootFrameworksOptions({ showShortName: true })];

export const getSortedVersions = (versionGroupCode: string | undefined): (Standard | Framework)[] => {
  if (!versionGroupCode) {
    return [];
  }
  const versions = [...Object.values(standards), ...Object.values(frameworks)].filter(p => p.versionGroupCode === versionGroupCode);
  return versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
}

export const getUtrFrameworkNames = (utr?: Pick<UniversalTrackerPlain, 'tags'>) => {
  const tags = utr?.tags;
  if (!tags) {
    return [];
  }

  return Object.values(frameworks)
    .filter((framework) => tags[framework.code as keyof Tags]?.length)
    .map(({ name }) => name);
};

export const getUtrStandardNames = (utr?: Pick<UniversalTrackerPlain, 'type' | 'name' | 'alternatives'>) => {
  if (!utr) {
    return [];
  }

  const names: string[] = [];
  if (standards[utr.type]) {
    names.push(standards[utr.type].name);
  }

  if (utr.alternatives) {
    Object.keys(utr.alternatives).forEach((key) => {
      if (standards[key]) {
        names.push(standards[key].name);
      }
    });
  }

  return Array.from(new Set(names));
};
